import { MontoColones } from '@/components/dominio/MontoColones';
import { MontoDolares } from '@/components/dominio/MontoDolares';
import {
  AYUDA_BANCOS,
  LineaCuentaDestino,
  SelectorBanco,
} from '@/components/flujo-semanal/BancoPago';
import { GenerarArchivoBancoSemanaDialog } from '@/components/flujo-semanal/GenerarArchivoBancoSemanaDialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useActualizarPago,
  useActualizarSaldos,
  useAsegurarSemanaActual,
  useCrearSemana,
  useResumenSemana,
  useSemanas,
  useSincronizarConceptos,
  useSugerirBancos,
  useTraerPendientes,
} from '@/hooks/useFlujo';
import { cliente } from '@/lib/cliente';
import type {
  BancoAsignado,
  GrupoPagos,
  Obligacion,
  PagoConDetalle,
  ResumenSemana,
  SubgrupoPagos,
} from '@adelante/shared';
import {
  formatearColones,
  formatearDolares,
  formatearRangoSemana,
  semanaDeFecha,
} from '@adelante/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  History,
  ListPlus,
  MessageSquare,
  Pencil,
  RefreshCw,
  Search,
  Sparkles,
  X,
} from 'lucide-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

type CampoEditable = 'adeudado' | 'aPagar' | 'pagado' | 'nota' | null;

// ─── Botón para generar siguiente cuota ──────────────────────────────────────

function FilaAgregarObligacion({
  conceptoId,
  montoCuotaFijo,
}: {
  conceptoId: number;
  montoCuotaFijo: number | null;
}) {
  const qc = useQueryClient();
  const [generando, setGenerando] = useState(false);
  const [montoInput, setMontoInput] = useState('');
  const [pendienteId, setPendienteId] = useState<number | null>(null);
  const [pendienteDesc, setPendienteDesc] = useState('');

  const handleGenerar = async () => {
    setGenerando(true);
    try {
      const res = await cliente.obligaciones.generarSiguiente(conceptoId);
      if (res.error) throw new Error(res.error.mensaje);
      const nueva = res.data!;

      if (nueva.monto !== null) {
        // Monto fijo: creada directamente, refrescar
        toast.success(`${nueva.periodoDescripcion} agregado`);
        qc.invalidateQueries({ queryKey: ['semana'] });
        qc.invalidateQueries({ queryKey: ['obligaciones'] });
      } else {
        // Variable: pedir monto
        setPendienteId(nueva.id);
        setPendienteDesc(nueva.periodoDescripcion);
        setMontoInput('');
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al generar');
    } finally {
      setGenerando(false);
    }
  };

  const handleGuardarMonto = async () => {
    if (!pendienteId) return;
    const clean = montoInput.trim().replace(/[₡$,\s]/g, '');
    const monto = clean === '' ? null : Number(clean);
    try {
      await cliente.obligaciones.actualizar(pendienteId, { monto });
      toast.success(`${pendienteDesc} agregado`);
      setPendienteId(null);
      qc.invalidateQueries({ queryKey: ['semana'] });
      qc.invalidateQueries({ queryKey: ['obligaciones'] });
    } catch {
      toast.error('Error al guardar monto');
    }
  };

  if (pendienteId) {
    return (
      <tr>
        <td colSpan={7} className="pb-2 pt-1 pl-8 pr-3">
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            <span className="text-xs text-amber-800 shrink-0">{pendienteDesc} —</span>
            <Input
              className="h-7 w-28 text-xs text-right tabular-nums border-amber-300"
              value={montoInput}
              onChange={(e) => setMontoInput(e.target.value)}
              placeholder="Monto (opcional)"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleGuardarMonto();
                if (e.key === 'Escape') setPendienteId(null);
              }}
            />
            <Button size="sm" className="h-7 px-2 text-xs" onClick={handleGuardarMonto}>
              OK
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="h-7 px-2 text-xs"
              onClick={() => setPendienteId(null)}
            >
              Cancelar
            </Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td colSpan={7} className="pb-1 pt-0.5 pl-8">
        <button
          type="button"
          className="flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
          onClick={handleGenerar}
          disabled={generando}
        >
          <span className="text-base leading-none">+</span>
          {generando
            ? 'Generando...'
            : montoCuotaFijo
              ? `Agregar siguiente cuota (₡${montoCuotaFijo.toLocaleString('es-CR')})`
              : 'Agregar siguiente período'}
        </button>
      </td>
    </tr>
  );
}

// ─── Fila de obligación individual (cuota) ────────────────────────────────────

function FilaObligacion({
  obl,
  semanaId,
  moneda,
}: {
  obl: Obligacion;
  semanaId: number;
  moneda: 'C' | 'D';
}) {
  const qc = useQueryClient();
  const [editandoMonto, setEditandoMonto] = useState(false);
  const [montoInput, setMontoInput] = useState('');
  const [guardando, setGuardando] = useState(false);
  const MontoComp = moneda === 'C' ? MontoColones : MontoDolares;

  const guardarMonto = async () => {
    const clean = montoInput.trim().replace(/[₡$,\s]/g, '');
    // Permite guardar blank (null) o cualquier número ≥ 0
    const monto = clean === '' ? null : Number(clean);
    if (monto !== null && Number.isNaN(monto)) {
      toast.error('Monto inválido');
      return;
    }
    setGuardando(true);
    try {
      const res = await cliente.obligaciones.actualizar(obl.id, { monto });
      if (res.error) throw new Error(res.error.mensaje);
      setEditandoMonto(false);
      qc.invalidateQueries({ queryKey: ['semana'] });
      qc.invalidateQueries({ queryKey: ['obligaciones'] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error');
    } finally {
      setGuardando(false);
    }
  };

  const handlePagarEstaSemana = async () => {
    if (obl.monto === null) {
      toast.error('Primero ingresá el monto de esta cuota');
      setEditandoMonto(true);
      return;
    }
    setGuardando(true);
    try {
      const res = await cliente.obligaciones.actualizar(obl.id, { semanaPagoId: semanaId });
      if (res.error) throw new Error(res.error.mensaje);
      toast.success(`${obl.periodoDescripcion} asignado a esta semana`);
      qc.invalidateQueries({ queryKey: ['semana'] });
      qc.invalidateQueries({ queryKey: ['obligaciones'] });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error');
    } finally {
      setGuardando(false);
    }
  };

  const yaAsignada = obl.semanaPagoId === semanaId;

  return (
    <tr
      className={`border-b last:border-0 transition-colors ${yaAsignada ? 'bg-blue-50/40' : 'hover:bg-muted/10'}`}
    >
      {/* Descripción del período — un nivel más adentro que el concepto.
          w-[30%]: mismo ancho que la primera columna de FilaPago (antes 40%, lo que desalineaba
          las columnas de montos de las cuotas respecto de las de los conceptos). */}
      <td className="py-2 pl-14 pr-3 w-[30%]">
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground shrink-0">↳</span>
          <span className="text-sm text-muted-foreground">{obl.periodoDescripcion}</span>
          {yaAsignada && (
            <Badge variant="secondary" className="text-xs py-0">
              Programado
            </Badge>
          )}
        </div>
      </td>

      {/* Monto — editable inline si es variable */}
      <td className="py-2 px-3 text-right w-[20%] border-l border-border/50">
        {editandoMonto ? (
          <div className="flex items-center gap-1 justify-end">
            <Input
              className="h-7 w-28 text-right text-xs tabular-nums"
              value={montoInput}
              onChange={(e) => setMontoInput(e.target.value)}
              placeholder="Monto"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') guardarMonto();
                if (e.key === 'Escape') setEditandoMonto(false);
              }}
            />
            <Button
              size="sm"
              className="h-7 px-2 text-xs"
              onClick={guardarMonto}
              disabled={guardando}
            >
              OK
            </Button>
          </div>
        ) : obl.monto === null ? (
          <button
            type="button"
            className="text-xs text-amber-600 font-medium underline-offset-2 hover:underline"
            onClick={() => {
              setMontoInput('');
              setEditandoMonto(true);
            }}
            title="Clic para ingresar el monto de esta cuota"
          >
            Ingresar monto →
          </button>
        ) : (
          <button
            type="button"
            className="text-xs text-muted-foreground tabular-nums text-right w-full hover:text-primary transition-colors"
            onClick={() => {
              setMontoInput(String(obl.monto));
              setEditandoMonto(true);
            }}
            title="Clic para editar"
          >
            <MontoComp monto={obl.monto} />
          </button>
        )}
      </td>

      {/* Columna A pagar vacía */}
      <td className="py-2 px-3 border-l border-border/50 w-[20%]" />

      {/* Acción — ocupa las 4 columnas restantes (Pagado, BAC, ✓, nota) para que la fila
          complete las mismas 7 columnas que las filas de concepto y no se corra la tabla. */}
      <td className="py-2 px-3 text-right border-l border-border/50 w-[30%]" colSpan={4}>
        {!yaAsignada ? (
          <Button
            size="sm"
            variant={obl.monto === null ? 'ghost' : 'outline'}
            className={`h-6 text-xs ${obl.monto === null ? 'text-muted-foreground' : ''}`}
            onClick={handlePagarEstaSemana}
            disabled={guardando}
            title={obl.monto === null ? 'Primero ingresá el monto' : ''}
          >
            {obl.monto === null ? 'Falta monto' : 'Pagar este viernes'}
          </Button>
        ) : (
          <Button
            size="sm"
            variant="ghost"
            className="h-6 text-xs text-muted-foreground"
            onClick={async () => {
              await cliente.obligaciones.actualizar(obl.id, { semanaPagoId: null });
              qc.invalidateQueries({ queryKey: ['semana'] });
            }}
          >
            Quitar
          </Button>
        )}
      </td>
    </tr>
  );
}

function NotaInline({
  nota,
  onGuardar,
  onCerrar,
}: {
  nota: string | null;
  onGuardar: (nota: string | null) => void;
  onCerrar: () => void;
}) {
  const [texto, setTexto] = useState(nota ?? '');
  return (
    <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
      <MessageSquare className="h-3.5 w-3.5 text-amber-500 shrink-0" />
      <Input
        className="h-7 text-xs border-0 bg-transparent focus-visible:ring-0 p-0 flex-1"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder="Agregar nota para esta semana..."
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') onGuardar(texto.trim() || null);
          if (e.key === 'Escape') onCerrar();
        }}
      />
      <Button
        size="sm"
        className="h-6 px-2 text-xs shrink-0"
        onClick={() => onGuardar(texto.trim() || null)}
      >
        Guardar
      </Button>
      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs shrink-0" onClick={onCerrar}>
        Cancelar
      </Button>
    </div>
  );
}

function parsearMonto(str: string): number | null {
  const n = Number(str.replace(/[₡$,\s]/g, ''));
  return Number.isNaN(n) ? null : n;
}

// Normaliza para búsqueda: minúsculas y sin acentos
function normalizar(s: string): string {
  // biome-ignore lint/suspicious/noMisleadingCharacterClass: rango de marcas diacríticas combinantes (U+0300-U+036F), correcto para strip de acentos tras normalize('NFD')
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

// Filtra los grupos dejando solo los pagos cuyo concepto coincide con la búsqueda
function filtrarGrupos(grupos: GrupoPagos[], termino: string): GrupoPagos[] {
  const q = normalizar(termino.trim());
  if (!q) return grupos;
  const coincide = (p: PagoConDetalle) => normalizar(p.concepto.nombre).includes(q);
  return grupos
    .map((g) => ({
      ...g,
      pagos: g.pagos.filter(coincide),
      subgrupos: g.subgrupos
        .map((sg) => ({ ...sg, pagos: sg.pagos.filter(coincide) }))
        .filter((sg) => sg.pagos.length > 0),
    }))
    .filter((g) => g.pagos.length > 0 || g.subgrupos.length > 0);
}

function CeldaSimple({
  monto,
  moneda,
  activo,
  onAbrir,
  onGuardar,
  onCerrar,
  placeholder,
}: {
  monto: number | null;
  moneda: 'C' | 'D';
  activo: boolean;
  onAbrir: () => void;
  onGuardar: (v: number) => void;
  onCerrar: () => void;
  placeholder?: string;
}) {
  const [val, setVal] = useState('');
  if (activo) {
    return (
      <div className="flex items-center gap-1 justify-end">
        <Input
          className="h-7 w-28 text-right text-xs tabular-nums"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              const n = parsearMonto(val);
              if (n !== null) onGuardar(n);
            }
            if (e.key === 'Escape') onCerrar();
          }}
          placeholder={placeholder ?? '0'}
          autoFocus
        />
        <Button
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => {
            const n = parsearMonto(val);
            if (n !== null) onGuardar(n);
          }}
        >
          OK
        </Button>
      </div>
    );
  }
  return (
    <button
      type="button"
      className="text-right w-full hover:text-primary transition-colors tabular-nums"
      onClick={() => {
        setVal(String(monto ?? ''));
        onAbrir();
      }}
      title="Clic para editar"
    >
      {moneda === 'C' ? <MontoColones monto={monto} /> : <MontoDolares monto={monto} />}
    </button>
  );
}

// Celda especial para "Adeudado" con selector de moneda integrado
function CeldaAdeudado({
  monto,
  monedaEfectiva,
  monedaConcepto,
  activo,
  onAbrir,
  onGuardar,
  onCerrar,
  onCambiarMoneda,
}: {
  monto: number | null;
  monedaEfectiva: 'C' | 'D';
  monedaConcepto: 'C' | 'D';
  activo: boolean;
  onAbrir: () => void;
  onGuardar: (v: number) => void;
  onCerrar: () => void;
  onCambiarMoneda: (m: 'C' | 'D') => void;
}) {
  const [val, setVal] = useState('');
  const [monedaLocal, setMonedaLocal] = useState<'C' | 'D'>(monedaEfectiva);

  const handleOk = () => {
    const n = parsearMonto(val);
    if (n !== null) {
      if (monedaLocal !== monedaConcepto) onCambiarMoneda(monedaLocal);
      else if (monedaLocal !== monedaEfectiva) onCambiarMoneda(monedaLocal);
      onGuardar(n);
    }
  };

  if (activo) {
    return (
      <div className="flex flex-col gap-1 items-end py-0.5">
        {/* Selector de moneda */}
        <div className="flex gap-0.5">
          <button
            type="button"
            onClick={() => setMonedaLocal('C')}
            className={`px-2 py-0.5 text-xs rounded border transition-colors ${
              monedaLocal === 'C'
                ? 'bg-blue-600 text-white border-blue-600'
                : 'border-border text-muted-foreground hover:border-blue-400'
            }`}
          >
            ₡
          </button>
          <button
            type="button"
            onClick={() => setMonedaLocal('D')}
            className={`px-2 py-0.5 text-xs rounded border transition-colors ${
              monedaLocal === 'D'
                ? 'bg-emerald-600 text-white border-emerald-600'
                : 'border-border text-muted-foreground hover:border-emerald-400'
            }`}
          >
            $
          </button>
          {monedaLocal !== monedaConcepto && (
            <button
              type="button"
              onClick={() => setMonedaLocal(monedaConcepto)}
              className="px-1.5 py-0.5 text-xs rounded border border-border text-muted-foreground hover:bg-muted"
              title="Volver a la moneda del concepto"
            >
              ↩
            </button>
          )}
        </div>
        {/* Input monto */}
        <div className="flex items-center gap-1">
          <Input
            className="h-7 w-28 text-right text-xs tabular-nums"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleOk();
              if (e.key === 'Escape') onCerrar();
            }}
            placeholder="Saldo adeudado"
            autoFocus
          />
          <Button size="sm" className="h-7 px-2 text-xs" onClick={handleOk}>
            OK
          </Button>
        </div>
      </div>
    );
  }

  return (
    <button
      type="button"
      className="text-right w-full hover:text-primary transition-colors tabular-nums group/adeudado"
      onClick={() => {
        setVal(String(monto ?? ''));
        setMonedaLocal(monedaEfectiva);
        onAbrir();
      }}
      title="Clic para editar — podés cambiar la moneda"
    >
      <span
        className={`mr-1 text-xs font-mono opacity-60 group-hover/adeudado:opacity-100 ${monedaEfectiva !== monedaConcepto ? 'text-amber-600 font-bold opacity-100' : ''}`}
      >
        {monedaEfectiva === 'C' ? '₡' : '$'}
      </span>
      {monedaEfectiva === 'C' ? <MontoColones monto={monto} /> : <MontoDolares monto={monto} />}
    </button>
  );
}

const PORCENTAJES = [
  { label: '25%', factor: 0.25 },
  { label: '33%', factor: 1 / 3 },
  { label: '50%', factor: 0.5 },
  { label: '67%', factor: 2 / 3 },
  { label: '75%', factor: 0.75 },
  { label: '100%', factor: 1 },
];

function CeldaAPagar({
  monto,
  adeudado,
  moneda,
  activo,
  onAbrir,
  onGuardar,
  onCerrar,
}: {
  monto: number | null;
  adeudado: number | null;
  moneda: 'C' | 'D';
  activo: boolean;
  onAbrir: () => void;
  onGuardar: (v: number) => void;
  onCerrar: () => void;
}) {
  const [val, setVal] = useState('');

  const aplicar = (v: number) => {
    onGuardar(Math.round(v * 100) / 100);
  };

  if (activo) {
    return (
      <div className="flex flex-col gap-1.5 items-end py-1">
        {adeudado !== null && adeudado > 0 && (
          <div className="flex flex-wrap gap-1 justify-end">
            {PORCENTAJES.map(({ label, factor }) => (
              <Button
                key={label}
                type="button"
                variant="outline"
                size="sm"
                className="h-6 px-2 text-xs font-normal"
                onClick={() => aplicar(adeudado * factor)}
              >
                {label}
              </Button>
            ))}
          </div>
        )}
        <div className="flex items-center gap-1">
          <Input
            className="h-7 w-28 text-right text-xs tabular-nums"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const n = parsearMonto(val);
                if (n !== null) aplicar(n);
              }
              if (e.key === 'Escape') onCerrar();
            }}
            placeholder="Monto manual"
            autoFocus
          />
          <Button
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => {
              const n = parsearMonto(val);
              if (n !== null) aplicar(n);
            }}
          >
            OK
          </Button>
        </div>
      </div>
    );
  }

  // Casi siempre se paga todo lo adeudado: un botón afuera lo deja con un solo clic (sin abrir el editor).
  const puedePagarTodo = adeudado !== null && adeudado > 0 && monto !== adeudado;

  return (
    <div className="flex items-center justify-end gap-1.5">
      {puedePagarTodo && (
        <button
          type="button"
          className="shrink-0 rounded border px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary hover:text-primary"
          onClick={() => aplicar(adeudado)}
          title="Pagar el monto completo adeudado"
        >
          Total
        </button>
      )}
      <button
        type="button"
        className="text-right hover:text-primary transition-colors tabular-nums font-medium"
        onClick={() => {
          setVal(String(monto ?? ''));
          onAbrir();
        }}
        title="Clic para editar — seleccioná porcentaje o ingresá manualmente"
      >
        {moneda === 'C' ? <MontoColones monto={monto} /> : <MontoDolares monto={monto} />}
      </button>
    </div>
  );
}

function FilaPagoResumen({ pago }: { pago: PagoConDetalle }) {
  const moneda = pago.concepto.moneda;
  return (
    <tr className="border-b last:border-0 bg-muted/10">
      <td className="py-2 pl-1 pr-3 w-[30%]">
        <span className="text-sm font-medium text-muted-foreground">{pago.concepto.nombre}</span>
      </td>
      <td className="py-2 px-3 text-right w-[20%] border-l border-border/50 text-muted-foreground">
        —
      </td>
      <td className="py-2 px-3 text-right w-[20%] border-l border-border/50 tabular-nums">
        {pago.montoProgramado !== null &&
          (moneda === 'C' ? (
            <MontoColones monto={pago.montoProgramado} />
          ) : (
            <MontoDolares monto={pago.montoProgramado} />
          ))}
      </td>
      <td className="py-2 px-3 text-right w-[14%] border-l border-border/50 tabular-nums">
        {pago.montoPagado !== null &&
          (moneda === 'C' ? (
            <MontoColones monto={pago.montoPagado} />
          ) : (
            <MontoDolares monto={pago.montoPagado} />
          ))}
      </td>
      <td className="py-2 pl-3 pr-1 text-center w-[10%] border-l border-border/50" />
      <td className="py-2 pl-3 pr-1 text-center w-[3%] border-l border-border/50" />
      <td className="py-2 pl-1 pr-1 text-center w-[3%]" />
    </tr>
  );
}

/**
 * Despacha entre la fila editable y la fila resumen de solo lectura.
 *
 * La rama vive acá, en un componente sin hooks, a propósito: si estuviera dentro
 * de `FilaPagoEditable` sería un `return` antes de sus hooks, y al cambiar una
 * fila de resumen a editable (o al revés) en la misma posición del árbol React
 * lanzaría "Rendered fewer hooks than expected".
 */
function FilaPago({ pago, semanaId }: { pago: PagoConDetalle; semanaId: number }) {
  if (pago.esResumen) return <FilaPagoResumen pago={pago} />;
  return <FilaPagoEditable pago={pago} semanaId={semanaId} />;
}

function FilaPagoEditable({ pago, semanaId }: { pago: PagoConDetalle; semanaId: number }) {
  const { mutate: actualizar, isPending } = useActualizarPago();
  const qc = useQueryClient();
  const [campo, setCampo] = useState<CampoEditable>(null);

  // La moneda efectiva: usa el override del pago, o la del concepto como default
  const monedaConcepto = pago.concepto.moneda;
  const monedaEfectiva: 'C' | 'D' = pago.monedaPago ?? monedaConcepto;

  const { mutate: crearPago } = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      cliente.pagos.crear({
        semanaId: pago.semanaId,
        conceptoId: pago.conceptoId,
        ...data,
      } as Parameters<typeof cliente.pagos.crear>[0]),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['semana'] }),
    onError: () => toast.error('Error al guardar'),
  });

  const esVirtual = pago.id < 0; // ID negativo = no tiene registro en BD todavía

  const guardar = (key: 'montoAdeudado' | 'montoProgramado' | 'montoPagado', valor: number) => {
    if (esVirtual) {
      crearPago(
        { [key]: valor },
        {
          onSuccess: () => {
            setCampo(null);
            toast.success('Guardado');
          },
        },
      );
    } else {
      actualizar(
        { id: pago.id, [key]: valor },
        {
          onSuccess: () => {
            setCampo(null);
            toast.success('Guardado');
          },
          onError: () => toast.error('Error al guardar'),
        },
      );
    }
  };

  const cambiarMoneda = (m: 'C' | 'D') => {
    if (esVirtual) return; // No hay registro aún, se asignará al primer guardar
    const monedaPago = m === monedaConcepto ? null : m;
    actualizar(
      { id: pago.id, monedaPago },
      { onError: () => toast.error('Error al cambiar moneda') },
    );
  };

  const moneda = monedaEfectiva;

  const handleConfirmar = () => {
    const nuevoPagado = !pago.pagado;
    if (esVirtual && nuevoPagado) {
      crearPago({ pagado: true });
      return;
    }
    // Al confirmar, auto-llena pagado con el monto a pagar si aún no tiene valor
    const payload =
      nuevoPagado && pago.montoPagado === null && pago.montoProgramado !== null
        ? { id: pago.id, pagado: true, montoPagado: pago.montoProgramado }
        : { id: pago.id, pagado: nuevoPagado };
    actualizar(payload, { onError: () => toast.error('Error') });
  };

  // Banco de este pago ('MANUAL' = a mano; null = vuelve al por defecto del concepto) y cuenta del
  // proveedor elegida a mano. Si el pago todavía no existe en la base, se crea con ese dato.
  const handleElegirBanco = (banco: BancoAsignado | null) => {
    if (esVirtual) {
      if (banco !== null) crearPago({ banco });
      return;
    }
    actualizar({ id: pago.id, banco }, { onError: () => toast.error('Error al cambiar el banco') });
  };

  const handleElegirCuenta = (proveedorCuentaId: number | null) => {
    if (esVirtual) {
      if (proveedorCuentaId !== null) crearPago({ proveedorCuentaId });
      return;
    }
    actualizar(
      { id: pago.id, proveedorCuentaId },
      { onError: () => toast.error('Error al cambiar la cuenta') },
    );
  };

  const oblPendientes = pago.obligacionesPendientes ?? [];
  const esObligacionConcepto = pago.concepto.tieneObligaciones;
  const tieneObls = esObligacionConcepto && oblPendientes.length > 0;
  const [oblsAbiertas, setOblsAbiertas] = useState(false);

  // Total de las cuotas pendientes (se muestra en la línea padre)
  const totalCuotas = oblPendientes.reduce((s, o) => s + (o.monto ?? 0), 0);
  const hayCuotaSinMonto = oblPendientes.some((o) => o.monto === null);

  return (
    <>
      <tr
        className={`border-b ${tieneObls ? '' : 'last:border-0'} transition-colors ${pago.pagado ? 'bg-green-50/40' : 'hover:bg-muted/20'}`}
      >
        {/* Concepto — indentado respecto de la categoría (que va sin sangría), y con un espacio
            fijo para el chevron de cuotas para que todos los nombres queden alineados, tengan
            cuotas o no. */}
        <td className="py-2 pl-6 pr-3 w-[30%]">
          <div className="flex items-center gap-1.5">
            {esObligacionConcepto ? (
              <button
                type="button"
                onClick={() => setOblsAbiertas((o) => !o)}
                className="text-muted-foreground hover:text-foreground shrink-0"
                title={oblsAbiertas ? 'Contraer cuotas' : 'Ver cuotas'}
              >
                {oblsAbiertas ? (
                  <ChevronDown className="h-3.5 w-3.5" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5" />
                )}
              </button>
            ) : (
              <span className="w-3.5 shrink-0" aria-hidden="true" />
            )}
            <span
              className={`text-sm font-medium ${pago.pagado ? 'line-through text-muted-foreground' : ''}`}
            >
              {pago.concepto.nombre}
            </span>
            {tieneObls && (
              <Badge variant="secondary" className="text-xs py-0 shrink-0">
                {oblPendientes.length} cuota{oblPendientes.length > 1 ? 's' : ''}
              </Badge>
            )}
          </div>
          <LineaCuentaDestino
            pago={pago}
            deshabilitado={isPending || pago.pagado}
            onElegirCuenta={handleElegirCuenta}
          />
        </td>
        {/* Adeudado — contabilidad (con selector de moneda).
          Para conceptos con cuotas (obligaciones) muestra la suma de las cuotas
          pendientes, que es el saldo realmente adeudado y no se edita a mano. */}
        <td className="py-2 px-3 text-right w-[20%] border-l border-border/50">
          {esObligacionConcepto ? (
            <div className="text-right tabular-nums" title="Suma de las cuotas pendientes">
              <span className="mr-1 text-xs font-mono opacity-60">
                {monedaEfectiva === 'C' ? '₡' : '$'}
              </span>
              {monedaEfectiva === 'C' ? (
                <MontoColones monto={totalCuotas} />
              ) : (
                <MontoDolares monto={totalCuotas} />
              )}
              {hayCuotaSinMonto && <span className="text-amber-600">+</span>}
            </div>
          ) : (
            <CeldaAdeudado
              monto={pago.montoAdeudado}
              monedaEfectiva={monedaEfectiva}
              monedaConcepto={monedaConcepto}
              activo={campo === 'adeudado'}
              onAbrir={() => setCampo('adeudado')}
              onGuardar={(v) => guardar('montoAdeudado', v)}
              onCerrar={() => setCampo(null)}
              onCambiarMoneda={cambiarMoneda}
            />
          )}
        </td>
        {/* A pagar esta semana — Luis */}
        <td className="py-2 px-3 text-right w-[20%] border-l border-border/50">
          <CeldaAPagar
            monto={pago.montoProgramado}
            adeudado={pago.montoAdeudado}
            moneda={moneda}
            activo={campo === 'aPagar'}
            onAbrir={() => setCampo('aPagar')}
            onGuardar={(v) => guardar('montoProgramado', v)}
            onCerrar={() => setCampo(null)}
          />
        </td>
        {/* Pagado — se auto-llena al confirmar, o editable */}
        <td className="py-2 px-3 text-right w-[14%] border-l border-border/50">
          <CeldaSimple
            monto={pago.montoPagado}
            moneda={moneda}
            activo={campo === 'pagado'}
            onAbrir={() => setCampo('pagado')}
            onGuardar={(v) => guardar('montoPagado', v)}
            onCerrar={() => setCampo(null)}
            placeholder="Auto al confirmar"
          />
        </td>
        {/* Banco — por dónde se paga esta semana (BAC, BN o a mano). Suma al total de ese banco para
            conciliar con su archivo. Un pago ya confirmado no cambia de banco. */}
        <td className="py-2 px-1 text-center w-[10%] border-l border-border/50">
          <SelectorBanco
            pago={pago}
            deshabilitado={isPending || pago.pagado}
            onElegir={handleElegirBanco}
          />
        </td>
        {/* ✓ Contabilidad confirma — auto-llena Pagado */}
        <td className="py-2 pl-3 pr-1 text-center w-[3%] border-l border-border/50">
          <Checkbox
            checked={pago.pagado}
            onCheckedChange={handleConfirmar}
            disabled={isPending}
            title="Contabilidad confirma — auto-llena monto pagado"
          />
        </td>
        {/* Nota */}
        <td className="py-2 pl-1 pr-1 text-center w-[3%]">
          <button
            type="button"
            title={pago.notas ? pago.notas : 'Agregar nota'}
            onClick={() => setCampo(campo === 'nota' ? null : 'nota')}
            className={`rounded p-0.5 transition-colors ${pago.notas ? 'text-amber-500 hover:text-amber-600' : 'text-muted-foreground/30 hover:text-muted-foreground'}`}
          >
            <MessageSquare className="h-3.5 w-3.5" />
          </button>
        </td>
      </tr>
      {tieneObls &&
        oblsAbiertas &&
        oblPendientes.map((obl) => (
          <FilaObligacion key={obl.id} obl={obl} semanaId={semanaId} moneda={monedaEfectiva} />
        ))}
      {esObligacionConcepto && oblsAbiertas && (
        <FilaAgregarObligacion
          conceptoId={pago.concepto.id}
          montoCuotaFijo={pago.concepto.montoCuotaFijo}
        />
      )}
      {campo === 'nota' && (
        <tr>
          <td colSpan={7} className="pb-2 px-1">
            <NotaInline
              nota={pago.notas}
              onGuardar={(nota) => {
                if (esVirtual) {
                  crearPago(
                    { notas: nota },
                    {
                      onSuccess: () => {
                        setCampo(null);
                      },
                    },
                  );
                } else {
                  actualizar(
                    { id: pago.id, notas: nota },
                    {
                      onSuccess: () => {
                        setCampo(null);
                        toast.success('Nota guardada');
                      },
                    },
                  );
                }
              }}
              onCerrar={() => setCampo(null)}
            />
          </td>
        </tr>
      )}
    </>
  );
}

function TablaSubgrupo({ subgrupo, semanaId }: { subgrupo: SubgrupoPagos; semanaId: number }) {
  return (
    // Sin `ml-4` en el contenedor: eso corría toda la tabla del subgrupo y descuadraba sus
    // columnas de montos respecto de las del resto. La sangría va solo en el título.
    <div className="mt-2">
      <div className="pl-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
        {subgrupo.categoria.nombre}
      </div>
      <table className="w-full">
        <tbody>
          {subgrupo.pagos.map((p) => (
            <FilaPago key={p.id} pago={p} semanaId={semanaId} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Dialog para agregar un pago esporádico ──────────────────────────────────

function DialogPagoAdicional({
  categoriaId,
  semanaId,
  onClose,
}: {
  categoriaId: number;
  semanaId: number;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    nombre: '',
    moneda: 'C',
    montoAdeudado: '',
    montoProgramado: '',
  });
  const [guardando, setGuardando] = useState(false);

  const handleGuardar = async () => {
    if (!form.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    setGuardando(true);
    try {
      const res = await cliente.pagosAdicionales.crear(semanaId, {
        categoriaId,
        nombre: form.nombre.trim(),
        moneda: form.moneda as 'C' | 'D',
        montoAdeudado: form.montoAdeudado
          ? Number(form.montoAdeudado.replace(/[₡$,\s]/g, ''))
          : null,
        montoProgramado: form.montoProgramado
          ? Number(form.montoProgramado.replace(/[₡$,\s]/g, ''))
          : null,
      });
      if (res.error) throw new Error(res.error.mensaje);
      toast.success('Pago adicional agregado');
      qc.invalidateQueries({ queryKey: ['semana'] });
      onClose();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Error al agregar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Agregar pago</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <Label>Nombre / Beneficiario *</Label>
            <Input
              value={form.nombre}
              onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))}
              placeholder="Ej: Luis Ortega, Cliente XYZ"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleGuardar()}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Moneda</Label>
              <div className="flex gap-1">
                {(['C', 'D'] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, moneda: m }))}
                    className={`flex-1 py-1.5 text-sm rounded border transition-colors ${form.moneda === m ? 'bg-primary text-primary-foreground border-primary' : 'border-border hover:bg-muted'}`}
                  >
                    {m === 'C' ? '₡ Colones' : '$ Dólares'}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-1">
              <Label>Monto adeudado</Label>
              <Input
                value={form.montoAdeudado}
                onChange={(e) => setForm((f) => ({ ...f, montoAdeudado: e.target.value }))}
                placeholder="0"
                className="tabular-nums"
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Monto a pagar esta semana</Label>
            <Input
              value={form.montoProgramado}
              onChange={(e) => setForm((f) => ({ ...f, montoProgramado: e.target.value }))}
              placeholder="0 (si es igual al adeudado, dejá vacío)"
              className="tabular-nums"
            />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={guardando}>
            Cancelar
          </Button>
          <Button onClick={handleGuardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Agregar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Suma las 3 columnas (Adeudado, A pagar, Pagado) separando colones y dólares.
// En conceptos con cuotas (obligaciones) el adeudado es la suma de las cuotas
// pendientes —igual que muestra la celda Adeudado—, no el montoAdeudado (null).
function sumarColumnas(pagos: PagoConDetalle[]) {
  const t = { adC: 0, adD: 0, prC: 0, prD: 0, pgC: 0, pgD: 0 };
  for (const p of pagos) {
    const m = p.monedaPago ?? p.concepto.moneda;
    const adeudado = p.concepto.tieneObligaciones
      ? (p.obligacionesPendientes ?? []).reduce((s, o) => s + (o.monto ?? 0), 0)
      : (p.montoAdeudado ?? 0);
    if (m === 'C') {
      t.adC += adeudado;
      t.prC += p.montoProgramado ?? 0;
      t.pgC += p.montoPagado ?? 0;
    } else {
      t.adD += adeudado;
      t.prD += p.montoProgramado ?? 0;
      t.pgD += p.montoPagado ?? 0;
    }
  }
  return t;
}

// Celda de total: muestra ₡ y/o $ apilados (— si ambos en cero)
function CeldaTotal({ c, d }: { c: number; d: number }) {
  if (!c && !d) return <span className="text-muted-foreground/40">—</span>;
  return (
    <div className="flex flex-col items-end leading-tight tabular-nums">
      {c > 0 && <span>{formatearColones(c)}</span>}
      {d > 0 && <span className="text-emerald-700">{formatearDolares(d)}</span>}
    </div>
  );
}

function GrupoCategoria({ grupo, semanaId }: { grupo: GrupoPagos; semanaId: number }) {
  const [agregando, setAgregando] = useState(false);

  // El backend cuenta los pagos reales ANTES de colapsar las categorías resumen: en
  // "Proveedores Crédito" contar las filas que llegan daría "x/2" en vez del número
  // de proveedores. Se cae al conteo local solo si el backend no mandó el dato.
  const totalPagados =
    grupo.pagosCompletados ??
    [...grupo.pagos, ...grupo.subgrupos.flatMap((sg) => sg.pagos)].filter((p) => p.pagado).length;

  const totalPagos =
    grupo.pagosEsperados ??
    grupo.pagos.length + grupo.subgrupos.reduce((s, sg) => s + sg.pagos.length, 0);
  const admiteAdicionales =
    grupo.categoria.admiteAdicionales ||
    grupo.subgrupos.some((sg) => sg.categoria.admiteAdicionales);

  // Totales de la categoría (Adeudado / A pagar / Pagado) — se muestran en la
  // misma línea del encabezado, alineados con las columnas de la tabla.
  const t = sumarColumnas([...grupo.pagos, ...grupo.subgrupos.flatMap((sg) => sg.pagos)]);

  // Las categorías con una subsección propia (Liquidaciones, Comisiones, Bonificaciones, Proveedores
  // Crédito, ...) se marcan con otro color: su monto sale de otra pantalla, no de los conceptos de la hoja.
  const conSubseccion = grupo.categoria.conDetalle || grupo.categoria.resumenEnSemana;

  return (
    <AccordionItem value={String(grupo.categoria.id)} className="border-b-0">
      {/* La fila de categoría va con fondo de color: eso marca la jerarquía (lo que tiene color
          es categoría, lo de adentro son conceptos) y hace innecesario repetir los títulos de
          columna dentro de cada categoría. Sin sangría extra a la izquierda: la categoría debe
          quedar MÁS a la izquierda que sus conceptos, no al revés. */}
      <AccordionTrigger
        hideChevron
        className={`hover:no-underline border-y py-2.5 [&[data-state=closed]_.chevron-cat]:-rotate-90 ${
          conSubseccion
            ? 'bg-lime-100 hover:bg-lime-200/70 border-l-4 border-l-lime-600'
            : 'bg-zinc-100 hover:bg-zinc-200/80'
        }`}
      >
        <div className="flex items-center w-full text-left">
          {/* Nombre + progreso */}
          <div className="w-[30%] flex items-center gap-2 pr-3">
            <ChevronDown className="chevron-cat h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200" />
            <span className="font-semibold text-sm uppercase tracking-wide">
              {grupo.categoria.nombre}
            </span>
            <span className="text-xs text-muted-foreground shrink-0">
              {totalPagados}/{totalPagos}
            </span>
            {conSubseccion && (
              <span
                className="shrink-0 rounded bg-lime-600/15 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-lime-800"
                title="El monto sale de su propia pantalla de detalle"
              >
                Detalle
              </span>
            )}
          </div>
          {/* Total Adeudado */}
          <div className="w-[20%] px-3 text-right border-l border-border/50 text-sm font-semibold">
            <CeldaTotal c={t.adC} d={t.adD} />
          </div>
          {/* Total A pagar */}
          <div className="w-[20%] px-3 text-right border-l border-border/50 text-sm font-semibold">
            <CeldaTotal c={t.prC} d={t.prD} />
          </div>
          {/* Total Pagado */}
          <div className="w-[14%] px-3 text-right border-l border-border/50 text-sm font-semibold">
            <CeldaTotal c={t.pgC} d={t.pgD} />
          </div>
          {/* Espacio de columnas BAC / ✓ / nota */}
          <div className="w-[10%] border-l border-border/50" />
        </div>
      </AccordionTrigger>
      <AccordionContent>
        {grupo.categoria.fuenteAutomatica === 'CONTADO' && (
          <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
            <span>El monto sale de las solicitudes de contado aprobadas esta semana.</span>
            <Link
              to="/vencimientos/solicitudes"
              className="rounded border bg-background px-2 py-1 font-medium text-foreground hover:bg-accent"
            >
              Ver solicitudes
            </Link>
          </div>
        )}
        {grupo.categoria.conDetalle && (
          <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
            <span>El monto sale del detalle de la semana.</span>
            <Link
              to={`/flujo-semanal/detalle/${grupo.categoria.id}?semana=${semanaId}`}
              className="rounded border bg-background px-2 py-1 font-medium text-foreground hover:bg-accent"
            >
              Ver detalle
            </Link>
          </div>
        )}
        {!grupo.categoria.conDetalle &&
          !grupo.categoria.fuenteAutomatica &&
          grupo.pagos.length === 0 &&
          grupo.subgrupos.length === 0 && (
            <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-4 py-2 text-xs text-muted-foreground">
              <span>Esta categoría todavía no tiene conceptos.</span>
              <Link
                to={`/flujo-semanal/conceptos?categoria=${grupo.categoria.id}&nuevo=1`}
                className="rounded border bg-background px-2 py-1 font-medium text-foreground hover:bg-accent"
              >
                Agregar concepto
              </Link>
            </div>
          )}
        {grupo.pagos.length > 0 && (
          <table className="w-full">
            <tbody>
              {grupo.pagos.map((p) => (
                <FilaPago key={p.id} pago={p} semanaId={semanaId} />
              ))}
            </tbody>
          </table>
        )}
        {grupo.subgrupos.map((sg) => (
          <TablaSubgrupo key={sg.categoria.id} subgrupo={sg} semanaId={semanaId} />
        ))}
        {admiteAdicionales && (
          <div className="pt-2 pb-1">
            <button
              type="button"
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary transition-colors px-1"
              onClick={() => setAgregando(true)}
            >
              <span className="text-base leading-none">+</span> Agregar pago
            </button>
          </div>
        )}
      </AccordionContent>
      {agregando && (
        <DialogPagoAdicional
          categoriaId={grupo.categoria.id}
          semanaId={semanaId}
          onClose={() => setAgregando(false)}
        />
      )}
    </AccordionItem>
  );
}

// ─── Dialog nueva semana ─────────────────────────────────────────────────────

function proximaSemana(semanas: import('@adelante/shared').Semana[]): {
  inicio: string;
  fin: string;
  semana: number;
  anio: number;
} {
  // El lunes siguiente al fin de la última semana (o de hoy, si no hay semanas), de lunes a domingo.
  const base = semanas[0] ? new Date(`${semanas[0].fechaFin}T12:00:00`) : new Date();
  const lunes = new Date(base);
  lunes.setDate(base.getDate() + ((8 - base.getDay()) % 7 || 7));
  const fecha = `${lunes.getFullYear()}-${String(lunes.getMonth() + 1).padStart(2, '0')}-${String(lunes.getDate()).padStart(2, '0')}`;
  const s = semanaDeFecha(fecha);
  return { inicio: s.fechaInicio, fin: s.fechaFin, semana: s.numero, anio: s.anio };
}

function DialogNuevaSemana({
  semanas,
  onClose,
  onCreada,
}: {
  semanas: import('@adelante/shared').Semana[];
  onClose: () => void;
  onCreada: (id: number) => void;
}) {
  const { mutate: crear, isPending } = useCrearSemana();
  const prox = proximaSemana(semanas);
  const ultimaSemana = semanas[0]; // más reciente
  const [form, setForm] = useState({
    numeroSemana: String(prox.semana),
    anio: String(prox.anio),
    fechaInicio: prox.inicio,
    fechaFin: prox.fin,
    tipoDeCambio: '',
    copiarPendientes: true,
  });

  const handleCrear = () => {
    const numSem = Number(form.numeroSemana);
    const anio = Number(form.anio);
    if (!numSem || !anio || !form.fechaInicio || !form.fechaFin) {
      toast.error('Todos los campos son requeridos');
      return;
    }
    const existe = semanas.find((s) => s.numeroSemana === numSem && s.anio === anio);
    if (existe) {
      toast.error(`La Semana ${numSem}/${anio} ya existe`);
      return;
    }

    crear(
      {
        numeroSemana: numSem,
        anio,
        fechaInicio: form.fechaInicio,
        fechaFin: form.fechaFin,
        tipoDeCambio: form.tipoDeCambio ? Number(form.tipoDeCambio) : null,
        copiarPendientesDeId: form.copiarPendientes && ultimaSemana ? ultimaSemana.id : null,
      },
      {
        onSuccess: (res) => {
          if (res.data?.id) {
            toast.success(`Semana ${numSem}/${anio} creada`);
            onCreada(res.data.id);
          }
        },
        onError: () => toast.error('Error al crear la semana'),
      },
    );
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Nueva Semana</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">N° Semana</Label>
              <Input
                value={form.numeroSemana}
                onChange={(e) => setForm((f) => ({ ...f, numeroSemana: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Año</Label>
              <Input
                value={form.anio}
                onChange={(e) => setForm((f) => ({ ...f, anio: e.target.value }))}
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Inicio (lunes)</Label>
              <Input
                type="date"
                value={form.fechaInicio}
                onChange={(e) => setForm((f) => ({ ...f, fechaInicio: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Fin (viernes)</Label>
              <Input
                type="date"
                value={form.fechaFin}
                onChange={(e) => setForm((f) => ({ ...f, fechaFin: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Tipo de Cambio (opcional)</Label>
            <Input
              value={form.tipoDeCambio}
              onChange={(e) => setForm((f) => ({ ...f, tipoDeCambio: e.target.value }))}
              placeholder="Ej: 520.50"
              className="tabular-nums"
            />
          </div>
          {/* Opción de copiar pendientes */}
          {ultimaSemana && (
            <label
              htmlFor="copiar-pendientes"
              className={`flex items-start gap-2.5 rounded-md border p-3 cursor-pointer transition-colors ${form.copiarPendientes ? 'border-primary/40 bg-primary/5' : 'border-border hover:bg-muted/30'}`}
            >
              <Checkbox
                id="copiar-pendientes"
                className="mt-0.5"
                checked={form.copiarPendientes}
                onCheckedChange={(checked) =>
                  setForm((f) => ({ ...f, copiarPendientes: checked === true }))
                }
              />
              <div>
                <p className="text-sm font-medium">
                  Copiar pagos pendientes de Semana {ultimaSemana.numeroSemana}/{ultimaSemana.anio}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Los pagos no confirmados (sin ✓) con monto programado se copian con su adeudado y
                  monto a pagar.
                </p>
              </div>
            </label>
          )}
          <p className="text-xs text-muted-foreground">
            Al crear la semana se generan automáticamente todas las filas de conceptos activos.
          </p>
        </div>
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            Cancelar
          </Button>
          <Button onClick={handleCrear} disabled={isPending}>
            {isPending ? 'Creando...' : 'Crear semana'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SemanaActualPantalla() {
  const { data: semanas, isLoading: cargandoSemanas } = useSemanas();
  const [semanaSeleccionada, setSemanaSeleccionada] = useState<number | null>(null);
  const [acordeonAbierto, setAcordeonAbierto] = useState<string[]>([]);
  const [busqueda, setBusqueda] = useState('');

  const semanaId = semanaSeleccionada ?? semanas?.[0]?.id ?? null;
  const { data: resumen, isLoading, refetch } = useResumenSemana(semanaId);
  const [dialogBancoAbierto, setDialogBancoAbierto] = useState(false);
  const [creandoSemana, setCreandoSemana] = useState(false);
  const { mutate: sincronizarConceptos, isPending: sincronizando } = useSincronizarConceptos();
  const { mutate: sugerirBancos, isPending: sugiriendo } = useSugerirBancos();
  const { mutate: traerPendientes, isPending: trayendo } = useTraerPendientes();
  const { mutate: asegurarSemana } = useAsegurarSemanaActual();

  // Al entrar se abre la semana de hoy si todavía no existe (cada lunes la primera persona que entra la crea).
  const apertura = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: se pide una sola vez al montar la pantalla
  useEffect(() => {
    if (apertura.current) return;
    apertura.current = true;
    asegurarSemana(undefined, {
      onSuccess: (r) => {
        if (r.data?.creada) {
          setSemanaSeleccionada(r.data.id);
          toast.success('Se abrió la semana de esta semana');
        }
      },
    });
  }, []);

  const handleTraerPendientes = () => {
    if (!semanaId) return;
    traerPendientes(semanaId, {
      onSuccess: (r) => {
        if (!r.data) {
          toast.error(r.error?.mensaje ?? 'No se pudieron traer los pendientes');
        } else if (!r.data.semanaOrigenId) {
          toast.info('No hay una semana anterior de donde traer pendientes');
        } else if (r.data.copiados === 0) {
          toast.info('No había pendientes por traer (o las filas ya tienen montos)');
        } else {
          toast.success(`${r.data.copiados} pendiente(s) traídos de la semana anterior`);
        }
      },
    });
  };

  const handleSugerirBancos = () => {
    if (!semanaId) return;
    sugerirBancos(semanaId, {
      onSuccess: (r) => {
        if (!r.data) {
          toast.error(r.error?.mensaje ?? 'No se pudieron sugerir los bancos');
          return;
        }
        const { asignados, sinSugerencia } = r.data;
        if (asignados.BAC + asignados.BN === 0) {
          toast.info(
            'Ningún pago pendiente tiene sugerencia: revisá que su proveedor y cuenta estén cargados',
          );
          return;
        }
        const aviso =
          sinSugerencia > 0
            ? ` · ${sinSugerencia} sin sugerencia (sin proveedor o sin cuenta)`
            : '';
        toast.success(`Asignados: ${asignados.BAC} a BAC y ${asignados.BN} a BN${aviso}`);
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Error al sugerir los bancos'),
    });
  };

  const handleSincronizarConceptos = () => {
    if (!semanaId) return;
    sincronizarConceptos(semanaId, {
      onSuccess: (res) => {
        const n = res.data?.agregados ?? 0;
        toast.success(
          n > 0
            ? `${n} concepto${n > 1 ? 's' : ''} agregado${n > 1 ? 's' : ''} a la semana`
            : 'La semana ya tiene todos los conceptos activos',
        );
      },
      onError: () => toast.error('Error al sincronizar conceptos'),
    });
  };

  // Inicializa el acordeón expandido cuando llegan los datos (o cambia la semana)
  // biome-ignore lint/correctness/useExhaustiveDependencies: solo re-expandir cuando cambia de semana, no en cada refetch de resumen
  useEffect(() => {
    if (resumen) setAcordeonAbierto(resumen.grupos.map((g) => String(g.categoria.id)));
  }, [resumen?.semana.id]);

  // ─── Panel Financiero ──────────────────────────────────────────────────────

  function FilaSaldo({
    label,
    valor,
    negrita = false,
    separador = false,
  }: {
    label: string;
    valor: string;
    negrita?: boolean;
    separador?: boolean;
  }) {
    return (
      <div
        className={`flex justify-between items-center py-1 ${separador ? 'border-t border-border mt-1 pt-2' : ''}`}
      >
        <span className={`text-sm ${negrita ? 'font-semibold' : 'text-muted-foreground'}`}>
          {label}
        </span>
        <span className={`text-sm tabular-nums ${negrita ? 'font-semibold' : ''}`}>{valor}</span>
      </div>
    );
  }

  function ModalSaldos({
    semanaId,
    semana,
    onClose,
  }: {
    semanaId: number;
    semana: ResumenSemana['semana'];
    onClose: () => void;
  }) {
    const { mutate: actualizar, isPending } = useActualizarSaldos();
    const [form, setForm] = useState({
      tipoDeCambio: String(semana.tipoDeCambio ?? ''),
      quinta: String(semana.quinta ?? ''),
      bancoColones: String(semana.bancoColones ?? ''),
      bancoDolares: String(semana.bancoDolares ?? ''),
    });

    const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
      setForm((f) => ({ ...f, [k]: e.target.value }));

    const parse = (v: string) => {
      const clean = v.replace(/[₡$,\s]/g, '');
      if (clean === '') return null;
      const n = Number(clean);
      return Number.isNaN(n) ? null : n;
    };

    const handleGuardar = () => {
      actualizar(
        {
          id: semanaId,
          tipoDeCambio: parse(form.tipoDeCambio),
          quinta: parse(form.quinta),
          bancoColones: parse(form.bancoColones),
          bancoDolares: parse(form.bancoDolares),
        },
        {
          onSuccess: () => {
            toast.success('Saldos actualizados');
            onClose();
          },
          onError: () => toast.error('Error al guardar'),
        },
      );
    };

    return (
      <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              Saldos Semana {semana.numeroSemana} · {semana.anio}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Tipo de Cambio (₡ por $1)</Label>
              <Input
                value={form.tipoDeCambio}
                onChange={set('tipoDeCambio')}
                placeholder="Ej: 520.50"
                className="tabular-nums"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">
                Quinta (ingreso Sociedad Quinta)
              </Label>
              <Input
                value={form.quinta}
                onChange={set('quinta')}
                placeholder="₡0"
                className="tabular-nums"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Bancos Colones</Label>
              <Input
                value={form.bancoColones}
                onChange={set('bancoColones')}
                placeholder="₡0"
                className="tabular-nums"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Bancos Dólares</Label>
              <Input
                value={form.bancoDolares}
                onChange={set('bancoDolares')}
                placeholder="$0"
                className="tabular-nums"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={isPending}>
              Cancelar
            </Button>
            <Button onClick={handleGuardar} disabled={isPending}>
              {isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  function PanelFinanciero({ resumen, semanaId }: { resumen: ResumenSemana; semanaId: number }) {
    const [editandoSaldos, setEditandoSaldos] = useState(false);

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Saldos disponibles */}
        <div className="border rounded-lg p-4 space-y-0.5">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Disponible
            </span>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs gap-1"
              onClick={() => setEditandoSaldos(true)}
            >
              <Pencil className="h-3 w-3" /> Editar
            </Button>
          </div>
          <FilaSaldo label="Quinta" valor={formatearColones(resumen.semana.quinta ?? 0)} />
          <FilaSaldo
            label="Bancos Colones"
            valor={formatearColones(resumen.semana.bancoColones ?? 0)}
          />
          <FilaSaldo
            label="Bancos Dólares"
            valor={formatearDolares(resumen.semana.bancoDolares ?? 0)}
          />
          <FilaSaldo
            label={'Tipo de Cambio'}
            valor={
              resumen.semana.tipoDeCambio
                ? `₡${resumen.semana.tipoDeCambio.toLocaleString('es-CR')}`
                : '—'
            }
          />
          <FilaSaldo
            label="Total Colonizado"
            valor={formatearColones(resumen.totalColonizado)}
            negrita
            separador
          />
        </div>

        {/* Totales a pagar y resultado */}
        <div className="border rounded-lg p-4 space-y-0.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground block mb-2">
            Resumen de Pagos
          </span>
          <FilaSaldo
            label="Total a Pagar ₡"
            valor={formatearColones(resumen.totalProgramadoColones)}
          />
          <FilaSaldo
            label="Total a Pagar $"
            valor={formatearDolares(resumen.totalProgramadoDolares)}
          />
          <FilaSaldo
            label="Total BAC ₡"
            valor={formatearColones(resumen.totalBacColones)}
            separador
          />
          <FilaSaldo label="Total BAC $" valor={formatearDolares(resumen.totalBacDolares)} />
          <FilaSaldo label="Total BN ₡" valor={formatearColones(resumen.totalBnColones)} />
          <FilaSaldo label="Total BN $" valor={formatearDolares(resumen.totalBnDolares)} />
          <FilaSaldo
            label="Total Pagos Colonizado"
            valor={formatearColones(resumen.totalPagosColonizado)}
            negrita
            separador
          />
          <FilaSaldo
            label="Disponible Bancos"
            valor={formatearColones(resumen.disponibleBancos)}
            separador
          />
          <FilaSaldo label="− Pagos" valor={formatearColones(resumen.totalPagosColonizado)} />
          <div
            className={`flex justify-between items-center py-1.5 mt-1 border-t-2 ${resumen.totalFinalCajas >= 0 ? 'border-green-400' : 'border-red-400'}`}
          >
            <span className="text-sm font-bold">TOTAL FINAL CAJAS</span>
            <span
              className={`text-sm font-bold tabular-nums ${resumen.totalFinalCajas >= 0 ? 'text-green-700' : 'text-red-600'}`}
            >
              {formatearColones(resumen.totalFinalCajas)}
            </span>
          </div>
        </div>

        {editandoSaldos && (
          <ModalSaldos
            semanaId={semanaId}
            semana={resumen.semana}
            onClose={() => setEditandoSaldos(false)}
          />
        )}
      </div>
    );
  }

  if (cargandoSemanas)
    return <div className="text-muted-foreground text-sm">Cargando semanas...</div>;

  if (!semanas?.length) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">No hay semanas registradas aún.</p>
      </div>
    );
  }

  const semanaActual = semanas.find((s) => s.id === semanaId);
  // `semanas` viene ordenado más reciente primero (semanas-listar.ts: ORDER BY anio DESC,
  // numero_semana DESC), así que "anterior" es el siguiente índice y "siguiente" el previo.
  const indiceSemana = semanas.findIndex((s) => s.id === semanaId);
  const semanaAnterior = indiceSemana >= 0 ? semanas[indiceSemana + 1] : undefined;
  const semanaSiguiente = indiceSemana > 0 ? semanas[indiceSemana - 1] : undefined;
  const porcentaje = resumen
    ? Math.round((resumen.pagosCompletados / Math.max(resumen.pagosTotal, 1)) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold">Pagos Semanales</h1>
          {semanaActual && (
            <p className="text-sm text-muted-foreground">
              Semana {semanaActual.numeroSemana} ·{' '}
              {formatearRangoSemana(semanaActual.fechaInicio, semanaActual.fechaFin)}
            </p>
          )}
        </div>
        <div className="flex w-full items-center gap-2 flex-wrap sm:w-auto sm:ml-auto">
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="icon"
              onClick={() => semanaAnterior && setSemanaSeleccionada(semanaAnterior.id)}
              disabled={!semanaAnterior}
              title="Semana anterior"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Select
              value={String(semanaId)}
              onValueChange={(v) => setSemanaSeleccionada(Number(v))}
            >
              <SelectTrigger className="w-44 sm:w-48">
                <SelectValue placeholder="Seleccionar semana" />
              </SelectTrigger>
              <SelectContent>
                {semanas.map((s) => (
                  <SelectItem key={s.id} value={String(s.id)}>
                    Semana {s.numeroSemana} · {s.anio}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="icon"
              onClick={() => semanaSiguiente && setSemanaSeleccionada(semanaSiguiente.id)}
              disabled={!semanaSiguiente}
              title="Semana siguiente"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
          <Button variant="outline" size="icon" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </Button>
          <Button
            variant="outline"
            onClick={handleSincronizarConceptos}
            disabled={sincronizando || !semanaId}
            className="gap-2"
            title="Agrega a esta semana los conceptos activos que falten (por ejemplo, conceptos creados después de la semana)"
          >
            <ListPlus className={`h-4 w-4 ${sincronizando ? 'animate-pulse' : ''}`} />
            Sincronizar conceptos
          </Button>
          <Button
            variant="outline"
            onClick={handleTraerPendientes}
            disabled={trayendo || !semanaId}
            className="gap-2"
            title="Copia a esta semana lo que quedó sin pagar en la semana anterior. Solo llena filas vacías: no cambia lo que ya digitaste."
          >
            <History className={`h-4 w-4 ${trayendo ? 'animate-pulse' : ''}`} />
            Traer pendientes
          </Button>
          <Button
            variant="outline"
            onClick={handleSugerirBancos}
            disabled={sugiriendo || !semanaId || !resumen?.pagosSinBanco}
            className="gap-2"
            title="Asigna BAC o BN a los pagos pendientes sin banco, según la cuenta de su proveedor. No cambia los que ya elegiste."
          >
            <Sparkles className={`h-4 w-4 ${sugiriendo ? 'animate-pulse' : ''}`} />
            Sugerir bancos
            {!!resumen?.pagosSinBanco && (
              <Badge variant="secondary" className="px-1.5 py-0 text-xs">
                {resumen.pagosSinBanco}
              </Badge>
            )}
          </Button>
          <Button
            variant="outline"
            onClick={() => setDialogBancoAbierto(true)}
            disabled={!semanaId}
            title="Genera el archivo de BAC (.prn) o del Banco Nacional (.TRF y .SIN) con los pagos de la semana"
            className="gap-2"
          >
            <Download className="h-4 w-4" />
            Archivos de banco
          </Button>
          <Button className="gap-2" onClick={() => setCreandoSemana(true)}>
            <span className="text-base leading-none">+</span>
            Nueva semana
          </Button>
        </div>
      </div>

      {/* Panel financiero */}
      {resumen && <PanelFinanciero resumen={resumen} semanaId={semanaId!} />}

      {/* Progreso */}
      {resumen && (
        <div className="space-y-1">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Progreso de pagos</span>
            <span className="font-medium">
              {resumen.pagosCompletados} / {resumen.pagosTotal} pagados
            </span>
          </div>
          <Progress value={porcentaje} />
        </div>
      )}

      {/* Tabla por categorías */}
      {isLoading ? (
        <div className="text-muted-foreground text-sm">Cargando pagos...</div>
      ) : resumen ? (
        (() => {
          const hayBusqueda = busqueda.trim().length > 0;
          const gruposFiltrados = filtrarGrupos(resumen.grupos, busqueda);
          // Al buscar, expandir automáticamente las categorías con coincidencias
          const valorAcordeon = hayBusqueda
            ? gruposFiltrados.map((g) => String(g.categoria.id))
            : acordeonAbierto;
          return (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <div className="relative flex-1 min-w-[180px] max-w-xs">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <Input
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar concepto..."
                    className="h-7 pl-8 pr-7 text-xs"
                  />
                  {hayBusqueda && (
                    <button
                      type="button"
                      onClick={() => setBusqueda('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      title="Limpiar búsqueda"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </div>
                <div className="ml-auto flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() =>
                      setAcordeonAbierto(resumen.grupos.map((g) => String(g.categoria.id)))
                    }
                    disabled={hayBusqueda}
                  >
                    Expandir todo
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => setAcordeonAbierto([])}
                    disabled={hayBusqueda}
                  >
                    Contraer todo
                  </Button>
                </div>
              </div>
              {hayBusqueda && gruposFiltrados.length === 0 ? (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No se encontraron conceptos que coincidan con «{busqueda.trim()}».
                </div>
              ) : (
                <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                  <div className="min-w-[680px]">
                    {/* Encabezado de columnas — una sola vez para toda la tabla (antes se
                        repetía dentro de cada categoría). Mismos anchos que las filas. */}
                    <div className="flex items-center w-full border-b pb-1.5 text-xs font-medium text-muted-foreground">
                      <div className="w-[30%]" />
                      <div
                        className="w-[20%] px-3 text-right border-l border-border/50"
                        title="Saldo total adeudado — lo ingresa contabilidad"
                      >
                        Adeudado
                      </div>
                      <div
                        className="w-[20%] px-3 text-right border-l border-border/50"
                        title="Monto a pagar esta semana — usá ÷2 ÷3 para dividir"
                      >
                        A pagar
                      </div>
                      <div
                        className="w-[14%] px-3 text-right border-l border-border/50"
                        title="Monto pagado / autorizado"
                      >
                        Pagado
                      </div>
                      <div
                        className="w-[10%] px-1 text-center border-l border-border/50"
                        title={AYUDA_BANCOS}
                      >
                        Banco
                      </div>
                      <div
                        className="w-[3%] px-1 text-center border-l border-border/50"
                        title="Contabilidad confirma"
                      >
                        ✓
                      </div>
                      <div className="w-[3%]" />
                    </div>
                    <Accordion
                      type="multiple"
                      value={valorAcordeon}
                      onValueChange={setAcordeonAbierto}
                    >
                      {gruposFiltrados.map((grupo) => (
                        <GrupoCategoria
                          key={grupo.categoria.id}
                          grupo={grupo}
                          semanaId={semanaId!}
                        />
                      ))}
                    </Accordion>
                  </div>
                </div>
              )}
            </>
          );
        })()
      ) : null}

      <GenerarArchivoBancoSemanaDialog
        semanaId={semanaId}
        open={dialogBancoAbierto}
        onClose={() => setDialogBancoAbierto(false)}
      />

      {creandoSemana && (
        <DialogNuevaSemana
          semanas={semanas}
          onClose={() => setCreandoSemana(false)}
          onCreada={(id) => {
            setSemanaSeleccionada(id);
            setCreandoSemana(false);
          }}
        />
      )}
    </div>
  );
}
